import { useState, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useTeamMember } from 'team/contexts/teamMemberContext'

/**
 * Custom hook to fetch and filter team members
 * @param {Object} team - Team object with _id
 * @returns {Object} { members, loading, searchQuery, setSearchQuery }
 */
export const useTeamMembers = (team) => {
    const { getMembers, teamMembers, loading } = useTeamMember()
    const location = useLocation()

    // Get subteamId from URL query params
    const subteamId = useMemo(() => {
        const params = new URLSearchParams(location.search)
        return params.get('subteamId')
    }, [location.search])

    // Fetch team members when component mounts or team changes
    // Pass subteamId to API for server-side filtering
    useEffect(() => {
        if (team && team._id) {
            getMembers(team._id, {
                ...(subteamId ? { subteamId } : {}),
                limit: 100
            })
        }
    }, [team, getMembers, subteamId])

    // Get current team members data (API should filter by subteamId, but we also guard client-side)
    const members = team && team._id ? teamMembers[team._id]?.members || [] : [];

    const filteredMembers = useMemo(() => {
        if (!subteamId) return members;
        return members.filter((member) => {
            const subteams = member?.subteamIds;
            if (!Array.isArray(subteams)) return false;
            return subteams.some((subteam) => {
                if (!subteam) return false;
                const candidateId = typeof subteam === 'string' ? subteam : subteam._id;
                return candidateId?.toString() === subteamId.toString();
            });
        });
    }, [members, subteamId]);

    return { members: filteredMembers, loading }
}

